/**
 * Design tokens do Hiplade — derivados do mockup Lovable (hiplade.lovable.app).
 *
 * Este módulo é a ÚNICA fonte de verdade de cor. Os valores são hex puro,
 * sem `!important`, para poderem ser usados livremente em `sx` do MUI v5.
 *
 * `./colors.ts` re-exporta estes mesmos valores com o sufixo ` !important`
 * exigido pelas folhas `makeStyles` (v4) legadas — não duplique valores lá.
 *
 * Os tokens do mockup são declarados em oklch; o hex abaixo é a conversão
 * (sRGB, gamma 2.4) de cada um deles.
 */

// ---------------------------------------------------------------- marca
/** `--brand` oklch(50% .24 290) — ação primária, links, ícones de conteúdo. */
const brand = '#6a30de'
/** `--brand-dark` oklch(35% .18 285) — títulos de seção e de card. */
const brandDark = '#391791'
/** `--brand-soft` / `--accent` oklch(94% .05 295) — fundos e chips suaves. */
const brandSoft = '#ede5ff'
/** `brand-soft/50` sobre `--page` — faixas de seção. */
const brandWash = '#f2ecff'
/** `--primary-foreground` oklch(99% 0 0). */
const brandForeground = '#fcfcfc'
/** Fim do gradiente do banner de evento — oklch(42% .22 290). */
const brandDeep = '#5418b9'

// ------------------------------------------------------------- superfície
/** `--page` oklch(96.5% .015 300) — fundo do documento. */
const page = '#f5f1fc'
/** `--background` oklch(98.5% .005 280) — inputs e barras translúcidas. */
const background = '#f9fafd'
/** `--card` — superfície de card. */
const surface = '#ffffff'
/** `--muted` oklch(95.5% .01 280). */
const muted = '#eef0f7'
/** `--border` / `--input` oklch(90% .015 280) — fio de 1px. */
const border = '#dcdde8'

// -------------------------------------------------------------- tipografia
/** `--foreground` oklch(22% .05 265) — texto corrido. */
const text = '#0f1932'
/** `--muted-foreground` oklch(50% .03 265) — texto secundário. */
const textMuted = '#5b6375'
/** Traço/ícone desabilitado. */
const textDisabled = '#b3b7c5'

// ----------------------------------------------------------------- sidebar
/** `--sidebar` oklch(19% .03 265). */
const sidebar = '#0d1321'
/** `--sidebar-accent` oklch(25% .04 265) — hover/ativo do item. */
const sidebarAccent = '#182135'
/** `--sidebar-foreground` oklch(92% .01 280). */
const sidebarText = '#e3e4eb'
/** Ícones do menu — `text-emerald-400`. */
const emerald = '#00d492'
/** `emerald-500` — badge sólido sobre escuro. */
const emeraldStrong = '#00bc7d'
/** `emerald-600` — texto/borda em verde sobre claro. */
const emeraldDeep = '#009966'
/** `emerald-100` — fundo suave de sucesso. */
const emeraldSoft = '#d0fae5'

// ------------------------------------------------------------------ estado
/** `--destructive` oklch(62% .22 20). */
const danger = '#ed324b'
/** `--danger-foreground` oklch(55% .2 20). */
const dangerText = '#cc243d'
/** `--danger-soft` oklch(96.5% .03 20). */
const dangerSoft = '#ffeceb'
/** Borda derivada de `--danger-foreground/20`. */
const dangerBorder = '#f2a7a6'
/** `--info-foreground` oklch(45% .15 240). */
const info = '#005b9f'
/** `--info-soft` oklch(96% .03 230). */
const infoSoft = '#def6ff'
/** `amber-600` — atenção / em correção. */
const warning = '#e17100'
/** `amber-100` — fundo de atenção. */
const warningSoft = '#fef3c6'

// -------------------------------------------------------------- debriefing
/** Casco do player — oklch(22% .03 265). */
const videoShell = '#141a29'
/** Barra de controles do player — oklch(28% .03 265). */
const videoBar = '#222938'
/** Início do banner do aluno — oklch(85% .08 295). */
const studentBannerFrom = '#d1c4fd'
/** Fim do banner do aluno — oklch(78% .1 290). */
const studentBannerTo = '#b6adf3'
/** Divisor/realce lilás intermediário. */
const lilacMid = '#a696e9'

// ------------------------------------------------------------------ rodapé
/** Faixa do rodapé — oklch(85% .02 290). */
const footerBand = '#cdccda'

/** Sombra padrão de card (`shadow-sm`). */
const shadowSm = '0 1px 2px 0 rgba(15, 25, 50, 0.06), 0 1px 3px 0 rgba(15, 25, 50, 0.08)'
/** Sombra de card em hover (`shadow-md`). */
const shadowMd = '0 4px 6px -1px rgba(15, 25, 50, 0.10), 0 2px 4px -2px rgba(15, 25, 50, 0.08)'
/** Raio padrão (`--radius` .5rem, aplicado em cards como 12px). */
const radius = 12

const palette = {
  brand,
  brandDark,
  brandSoft,
  brandWash,
  brandForeground,
  brandDeep,
  page,
  background,
  surface,
  muted,
  border,
  text,
  textMuted,
  textDisabled,
  sidebar,
  sidebarAccent,
  sidebarText,
  emerald,
  emeraldStrong,
  emeraldDeep,
  emeraldSoft,
  danger,
  dangerText,
  dangerSoft,
  dangerBorder,
  info,
  infoSoft,
  warning,
  warningSoft,
  videoShell,
  videoBar,
  studentBannerFrom,
  studentBannerTo,
  lilacMid,
  footerBand,
  shadowSm,
  shadowMd,
  radius,
}

export default palette
